import { Mail, MessageCircle } from 'lucide-react';
import { useAuth } from '../../../lib/auth-context.js';
import { Button } from '../../../components/ui/button.js';
import { useVersion } from '../../common/hooks.js';

/**
 * Get-support landing. Captures the fingerprint customers should
 * include in tickets; the CTA opens the local mail client with the
 * fingerprint pre-filled. Stage 2 wires a `POST /v1/support/ticket`
 * to file the ticket directly without round-tripping through email.
 */
export function SupportGetSupportPage(): JSX.Element {
  const { session } = useAuth();
  const { data: version } = useVersion();

  const fingerprint = [
    `License: ${session?.licenseId ?? '—'}`,
    `User: ${session?.email ?? session?.workfrontUserId ?? '—'}`,
    `Env: ${version?.env ?? '—'}`,
    `Commit: ${version?.commitSha ?? '—'}`,
    `Timestamp: ${new Date().toISOString()}`,
  ].join('\n');

  const subject = encodeURIComponent('WorkFocus support request');
  const body = encodeURIComponent(`Describe the issue:\n\n\n\n---\n${fingerprint}`);

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Get Support</h1>
        <p className="text-sm text-foreground/60">
          Describe what isn&apos;t working — your environment fingerprint is included automatically
          so support can find your deployment.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 px-6 py-3 lg:grid-cols-2">
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
            <Mail className="h-4 w-4" /> Email support
          </header>
          <p className="text-sm text-foreground/60">
            Opens your default mail client with the fingerprint pre-filled in the body. Add a
            description of the issue at the top.
          </p>
          <a
            href={`mailto:support@workfocus.io?subject=${subject}&body=${body}`}
            className="mt-3 inline-block"
          >
            <Button
              type="button"
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              <Mail className="mr-1.5 h-4 w-4" /> Open support email
            </Button>
          </a>
        </section>

        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
            <MessageCircle className="h-4 w-4" /> In-app ticket
          </header>
          <p className="text-sm text-foreground/60">
            Stage 2 wires a direct ticket-filing form here — same fingerprint, no mail-client
            detour.
          </p>
          <Button
            type="button"
            disabled
            variant="outline"
            className="mt-3 border-foreground/15 text-foreground/50"
          >
            <MessageCircle className="mr-1.5 h-4 w-4" /> Open ticket form
          </Button>
        </section>
      </div>

      <div className="px-6 py-3">
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-2 text-sm font-medium text-foreground/80">
            Fingerprint (preview)
          </header>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-foreground/10 bg-black/30 p-3 font-mono text-xs text-foreground/75">
            {fingerprint}
          </pre>
        </section>
      </div>
    </div>
  );
}
